import {
  Badge,
  Box,
  Button,
  Group,
  Input,
  Menu,
  MultiSelect,
  MultiSelectProps,
  rem,
  Text,
} from "@mantine/core";
import {
  IconBarcode,
  IconBuildingSkyscraper,
  IconBuildingWarehouse,
  IconCaretDown,
  IconFileExport,
  IconMapPin,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import {
  repositoryDelivery,
  repositoryMdm,
  repositoryPos,
  styleTableMantine,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { paginationBase } from "../../../_base/model/_base/BaseTable";
import { _localization } from "../../../config/location";
import { SelectListItemModel } from "../../../model/SelectListItem";
import { exportDataToExcel } from "../../../common/ExportExcelToAPI/ExportExcelToAPI";
import { green } from "../../../const/variables";
import { BASE_API_DELIVERY, BASE_API_MDM } from "../../../config";
import { useForm } from "@mantine/form";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import { useDebouncedCallback } from "@mantine/hooks";

interface SearchCriteria {
  keySearch?: string;
  branchCode?: string[]; // Mảng chứa các mã chi nhánh
  invCode?: string[]; // Mảng chứa các mã kho
  itemCode?: string[]; // Mảng chứa các mã sản phẩm
  itemKey?: string; // Mã sản phẩm hoặc khóa sản phẩm
}

const InventoryBarcode = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [data, setData] = useState<InventoryItem[]>([]);
  const [dataInvCode, setDataInvCode] = useState<SelectListItemModel[]>([]);
  const [dataItemCode, setDataItemCode] = useState<SelectListItemModel[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [dataSaleChannel, setDataBranchCode] = useState<SelectListItemModel[]>(
    []
  );
  const form = useForm<SearchCriteria>({
    mode: "uncontrolled",
    initialValues: {},
  });
  const formatCurrencyVND = (value: any) => {
    if (value == null) {
      return "";
    }
    // Định dạng số theo chuẩn Việt Nam
    return value.toLocaleString("vi-VN", {
      minimumFractionDigits: 0, // Không hiển thị phần thập phân
      maximumFractionDigits: 0, // Đảm bảo không có chữ số sau dấu phẩy
    });
  };
  type InventoryItem = {
    maCn: string;
    tenCn: string;
    maKho: string;
    tenKho: string;
    maViTri: string;
    tenViTri: string | null;
    maSp: string;
    tenSp: string;
    dvt: string;
    serial: string;
    serialNote: string;
    serialStatus: string | null;
    soPn: string;
    ngayNhap: string;
    tuoiTon: number;
    nhaCung: string;
    sl: number;
    giaNhap: number;
    serialCha: string | null;
    ngayCapNhatCuoi: string | null;
  };

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "maSp",
        header: "Mã sản phẩm",
        Cell: ({ renderedCellValue, cell, row }) => (
          <>
            <Badge
              radius="sm"
              variant="dot"
              size="md"
              style={{ cursor: "pointer" }}
              color={renderedCellValue === null ? "red" : green.base}
            >
              {renderedCellValue ?? "Không có mã sản phẩm"}
            </Badge>
          </>
        ),
      },
      {
        accessorKey: "maCn",
        header: "Mã chi nhánh",
      },
      {
        accessorKey: "tenCn",
        header: "Tên chi nhánh",
      },
      {
        accessorKey: "maKho",
        header: "Mã kho",
        size: 150,
      },
      {
        accessorKey: "tenKho",
        header: "Tên kho",
        size: 225,
      },
      {
        accessorKey: "maViTri",
        header: "Mã vị trí",
      },
      {
        accessorKey: "tenViTri",
        header: "Tên vị trí",
      },
      {
        accessorKey: "tenSp",
        header: "Tên sản phẩm",
      },

      {
        accessorKey: "serialCha",
        header: "Mã vạch cha",
      },
      {
        accessorKey: "serial",
        header: "Mã vạch",
      },
      {
        accessorKey: "serialNote",
        header: "Ghi chú mã vạch",
      },
      {
        accessorKey: "serialStatus",
        header: "Trạng thái mã vạch",
      },

      {
        accessorKey: "soPn",
        header: "Số phiếu nhập",
      },

      {
        accessorKey: "dvt",
        header: "Đơn vị tính",
      },
      {
        accessorKey: "tuoiTon",
        header: "Tuổi tồn",
      },
      {
        accessorKey: "nhaCung",
        header: "Nhà cung cấp",
      },
      {
        accessorKey: "sl",
        header: "Số lượng",
      },
      {
        accessorKey: "giaNhap",
        header: "Giá nhập",
        Cell: ({ renderedCellValue }: any) => (
          <>{formatCurrencyVND(renderedCellValue)}</>
        ),
      },
      {
        accessorKey: "ngayNhap",
        header: "Ngày nhập",
        Cell: ({ renderedCellValue }: any) => (
          <>{renderedCellValue && formatDateTransfer(renderedCellValue)}</>
        ),
      },
      {
        accessorKey: "ngayCapNhatCuoi",
        header: "Ngày cập nhật cuối",
        Cell: ({ renderedCellValue }: any) => (
          <>{renderedCellValue && formatDateTransfer(renderedCellValue)}</>
        ),
      },
    ],
    []
  );
  const exportDataExcel = async () => {
    let url = `?Skip=${pagination?.pageIndex * pagination?.pageSize}&Take=${
      pagination.pageSize
    }`;
    const search = form.getValues();
    if (search.branchCode && search.branchCode.length > 0) {
      search.branchCode.forEach((branchCode) => {
        url += `&MaCns=${branchCode}`;
      });
    }
    if (search.invCode && search.invCode.length > 0) {
      search.invCode.forEach((invCode) => {
        url += `&MaKhos=${invCode}`;
      });
    }
    if (search.itemCode && search.itemCode.length > 0) {
      search.itemCode.forEach((itemCode) => {
        url += `&MaSps=${itemCode}`;
      });
    }
    if (search.keySearch) {
      url += `&KeySearch=${search.keySearch.trim().toLowerCase()}`;
    }
    const baseUrl = `/api/v1/ExportExcel/export-serial-list${url}`;

    const _url = BASE_API_MDM + baseUrl;
    const token = localStorage.getItem("token");

    const response = await fetch(_url, {
      method: "GET",
      headers: {
        "Content-Type": "application/vnd.ms-excel",
        Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to download file");
    }
    const blob = await response.blob();
    const urlss = window.URL.createObjectURL(blob);
    const _name = new Date().toISOString().slice(0, 10);
    let filename = "bao_cao_danh_sach_ma_vach.xlsx";
    const link = document.createElement("a");
    link.href = urlss;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };
  // Hàm lấy dữ liệu
  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);

    let url = `?Skip=${pagination?.pageIndex * pagination?.pageSize}&Take=${
      pagination.pageSize
    }`;
    const search = form.getValues();
    if (search.branchCode && search.branchCode.length > 0) {
      search.branchCode.forEach((branchCode) => {
        url += `&MaCns=${branchCode}`;
      });
    }
    if (search.invCode && search.invCode.length > 0) {
      search.invCode.forEach((invCode) => {
        url += `&MaKhos=${invCode}`;
      });
    }
    if (search.itemCode && search.itemCode.length > 0) {
      search.itemCode.forEach((itemCode) => {
        url += `&MaSps=${itemCode}`;
      });
    }
    if (search.keySearch) {
      url += `&KeySearch=${search.keySearch.trim().toLowerCase()}`;
    }
    try {
      const response = await repositoryMdm.get(
        `/api/v1/SerialLists/get-list${url}`
      );
      if (response && response.success) {
        let result = response.data;
        setData(result);
        setRowCount(response.totalCount);
        table.resetRowSelection();
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };
  const DataBrachCode = async () => {
    try {
      const response = await repositoryPos.get(
        `api/v1/TblDmInventory/get-select-branch?auth=true`
      );
      if (response && response.success) {
        const transformedData = response.data
          .filter((i: any) => i.att2 && i.text)
          .map((item: any) => {
            return {
              value: item.att2,
              label: item.att2 + " - " + item.text,
            };
          });

        setDataBranchCode(transformedData);
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };
  const DataInvCode = async () => {
    try {
      const response = await repositoryPos.get(
        `/api/v1/TblDmInventory/get-all?checkAuth=true`
      );
      if (response && response.httpStatusCode === 200) {
        const transformedData = response.data
          .filter((i: any) => i.att1 && i.text)
          .map((item: any) => {
            return {
              value: item.att1,
              label: item.text,
            };
          });

        setDataInvCode(transformedData);
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };
  const DataItemCode = async (keySearch: string) => {
    try {
      let url = "";
      if (keySearch && keySearch.length > 0) {
        url += `&ItemCodes=${keySearch}`;
      }
      // const response = await repositoryDelivery.get(
      //   `api/v1/TblInventorySerialOnhand/get-list-item-by-inv?Skip=0&Take=50` +
      //     url
      // );
      const response = await repositoryPos.get(
        `/api/v1/TblItem/get-select?Key=${keySearch}&Page=30`
      );
      if (response && response.success) {
        // const transformedData = response.data
        //   ?.filter((i: any) => i.maSp && i.tenSp)
        //   .map((branch: any) => ({
        //     value: branch.maSp,
        //     label: branch.maSp + " - " + branch.tenSp,
        //   }));
        const transformedData = response.data
          ?.filter((i: any) => i.value && i.text)
          .map((branch: any) => ({
            value: branch.value,
            label: branch.text,
          }));
        const uniqueData = Array.from(
          new Set(transformedData.map((item: any) => item.value))
        ).map((value) =>
          transformedData.find((item: any) => item.value === value)
        );
        setDataItemCode(uniqueData);
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: true,
    rowCount,
    enableColumnFilters: true,
    enableSorting: true,
    enableColumnActions: true,
    // manualPagination: true,

    state: {
      // columnFilters,
      // globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      rowSelection,
    },
    enableStickyFooter: true,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    onPaginationChange: setPagination,
    positionToolbarAlertBanner: "bottom",
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["maSp", "transactionNumber", "sourceCode"],
        right: ["statusName"],
      },
      columnVisibility: { id: false },
      density: "xs",
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <div ref={headerRef}>
        <_breadcrumb></_breadcrumb>
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <Input
            style={{ flex: 1, maxWidth: "180px", minWidth: "180px" }}
            placeholder="Nhập tìm kiếm"
            key={form.key("keySearch")}
            type="text"
            leftSection={<IconSearch size={"20"} color="#15aabf" />}
            onKeyDown={async (e) => {
              if (e.key === "Enter") {
                await fetchData();
              }
            }}
            {...form.getInputProps("keySearch")}
          />
          <MultiSelect
            key={form.key("branchCode")}
            placeholder="Chi nhánh"
            data={dataSaleChannel.map((x) => x.value)}
            leftSection={<IconBuildingSkyscraper size={20} color="#15aabf" />}
            clearable
            renderOption={renderMultiSelectOptionbrachcode}
            searchable
            onClick={() => {
              if (dataSaleChannel.length === 0) {
                DataBrachCode();
              }
            }}
            {...form.getInputProps("branchCode")}
          />
          <MultiSelect
            key={form.key("invCode")}
            size="sm"
            placeholder="Mã kho"
            searchable
            clearable
            data={dataInvCode.map((x) => x.value)}
            renderOption={renderMultiSelectOptionInvCode}
            leftSection={<IconBuildingWarehouse size={20} color="#15aabf" />}
            onClick={() => {
              if (dataInvCode.length === 0) {
                DataInvCode();
              }
            }}
            {...form.getInputProps("invCode")}
          />

          <MultiSelect
            key={form.key("itemCode")}
            size="sm"
            placeholder="Mã sản phẩm"
            clearable
            searchable
            data={dataItemCode.map((x) => x.value)}
            renderOption={renderMultiSelectOption}
            leftSection={<IconBarcode size={20} color="#15aabf" />}
            onSearchChange={(value) => {
              // if (value.length >= 3) {
              //   DataItemCode(value);
              // }
              debouncedDataItemCode(value);
            }}
            {...form.getInputProps("itemCode")}
          />

          <Button
            color="blue"
            variant="outline"
            onClick={async () => {
              await fetchData();
            }}
          >
            Tìm kiếm
          </Button>
        </Box>
      </div>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <>
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <Button
              // size="xs"
              rightSection={
                <IconCaretDown style={{ width: rem(14), height: rem(14) }} />
              }
            >
              Chức năng
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label>Application</Menu.Label>
            <Menu.Item
              leftSection={
                <IconFileExport style={{ width: rem(14), height: rem(14) }} />
              }
              onClick={() => exportDataExcel()}
            >
              Export Excel
            </Menu.Item>
            <Menu.Item
              leftSection={
                <IconX style={{ width: rem(14), height: rem(14) }} />
              }
            >
              Đóng
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </>
    ),

    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    getRowId: (row) => row.departmentId,
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    mantineTableBodyCellProps: ({ row }) => ({
      style: styleTableMantine,
    }),
    mantineToolbarAlertBannerProps: false
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,

    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "xl",
      size: "lg",
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: row.getToggleSelectedHandler(),
      sx: { cursor: "pointer" },
    }),
  });
  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (185 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);
  useEffect(() => {
    fetchData();
  }, [pagination]);
  const renderMultiSelectOption: MultiSelectProps["renderOption"] = ({
    option,
  }) => (
    <Group gap="sm">
      <Text>{dataItemCode.find((x) => x.value === option.value)?.label}</Text>
    </Group>
  );
  const renderMultiSelectOptionInvCode: MultiSelectProps["renderOption"] = ({
    option,
  }) => (
    <Group gap="sm">
      <Text>
        {dataInvCode.find((x: any) => x.value === option.value)?.label}
      </Text>
    </Group>
  );
  const renderMultiSelectOptionbrachcode: MultiSelectProps["renderOption"] = ({
    option,
  }) => (
    <Group gap="sm">
      <Text>
        {dataSaleChannel.find((x: any) => x.value === option.value)?.label}
      </Text>
    </Group>
  );

  // Sử dụng debounce để trì hoãn việc gọi API
  const debouncedDataItemCode = useDebouncedCallback((value: string) => {
    if (value.length >= 3) {
      DataItemCode(value); // Gọi API với từ khóa tìm kiếm
    }
  }, 500);
  return (
    <Box>
      <MantineReactTable table={table} />
    </Box>
  );
};

export default InventoryBarcode;
